"use client";
import { useCallback, useMemo, useState } from "react";
import map from "lodash/map";
import { css, styled, Theme } from "@mui/material/styles";
import {
  cleanInteger,
  formatDate,
  formatTime,
  parseDate,
  fromDOBDisplay,
  parsePhoneNumber,
  renderDate,
  toDOBDisplay,
  renderDollarAmount,
  renderPhoneNumber,
} from "@portal/app/_common/lib/formatRenderers";
import { useField } from "formik";
import { DateTime } from "luxon";

type InputValue = string | number | DateTime | null | undefined;

const sharedTextStyles = (theme: Theme) => css`
  width: 100%;
  padding: 1rem;
  border: 1px solid ${theme.palette.dream.grayMedium};
  border-radius: 8px;
  font-family: ${theme.typography.fontFamily};
  font-size: 1.1rem;
  background: #fff;
  box-shadow: 0 2px 3px rgba(0, 0, 0, 0.02);
  cursor: pointer;
  transition: 100ms linear all;

  textarea& {
    min-height: 8rem;
    resize: vertical;
    max-height: 50vh;
  }

  &:not(:disabled) {
    &:hover {
      border-color: ${theme.palette.dream.grayMediumDark};
    }

    &:focus {
      outline: none;
      border-color: ${theme.palette.primary.main};
      box-shadow: 0 2px 3px rgba(0, 0, 0, 0.1);
      cursor: text;
    }
  }

  &:disabled {
    color: ${theme.palette.dream.grayMedium};
    background: ${theme.palette.dream.grayLighter};
    cursor: not-allowed;
  }

  &::placeholder {
    color: ${theme.palette.dream.grayMedium};
  }

  &.-hasError {
    border-color: ${theme.palette.error.main};
  }

  &.mono {
    font-family: ${theme.typography.mono.fontFamily};
    font-size: 1rem;
  }

  &.small-font {
    font-size: 0.9rem;
  }
`;

export const FormInputText = styled("input")(({ theme }) => sharedTextStyles(theme));

export const FormInputTextArea = styled("textarea")(({ theme }) => sharedTextStyles(theme));

type FormInputWithParseProps<Value extends InputValue> = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "value"
> & {
  value?: Value;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  $toDisplay?: (value?: Value) => string;
  $fromDisplay?: (input?: string) => Value;
  as?: React.ElementType;
};

/**
 * Same as FormInputText, except that it allows for custom rendering and parsing behavior
 * as the user types (for actions on blur, see FormInputWithParseOnBlur).
 */
export const FormInputWithParse = <Value extends InputValue>({
  value = "",
  onChange,
  $toDisplay = (v) => v as string,
  $fromDisplay = (v) => v as Value,
  as: Element = FormInputText,
  ...propsToElement
}: FormInputWithParseProps<Value>) => {
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      event.target.value = `${$fromDisplay(event.target.value)}`;
      onChange?.(event);
    },
    [$fromDisplay, onChange]
  );

  return (
    <Element
      value={$toDisplay(value as Value) || ""} // TODO: Remove cast
      onChange={handleChange}
      {...propsToElement}
    />
  );
};

type FormInputWithParseOnBlurProps<Value extends InputValue> = Omit<
  React.ComponentProps<typeof FormInputText>,
  "value"
> & {
  value?: Value | "";
  // This is not a ChangeEventHandler. This handler passes you the user-inputted value, and you can
  // optionally pass back a string which will be written back to the displayed value.
  onChange?: (newValue: Value) => void;
  $toDisplay?: (value: Value) => string;
  $fromDisplay?: (input?: string) => Value;
  // The `$toDisplay` formatter will only run when the user blurs the field. For formatting while the
  // user is still typing, pass a formatter here.
  toDisplayPartial?: (input: string) => string;
  /** Observe raw edits without replacing the local display or waiting for blur. */
  onInputChange?: (input: string) => void;
  /** Keep edited text when the parsed value cannot be rendered on blur. */
  preserveUnparsedInput?: boolean;
  as?: React.ElementType;
};

/**
 * Same as FormInputText, except that it allows for custom rendering and parsing behavior
 * when the user blurs the field (for actions while typing, see FormInputWithParse).
 *
 * The `onChange` handler is not a ChangeEventHandler, and it will only surface the value back to you after
 * the field is blurred. This is useful for "snapping" user inputs back to a parsed value, as
 * validation and formatting only runs on blur.
 */

const FormInputWithParseOnBlur = <Value extends InputValue>({
  value = "",
  onChange,
  $toDisplay = (v) => v as string, // TODO: Remove cast
  $fromDisplay = (v) => v as Value, // TODO: Remove cast
  toDisplayPartial = (v) => v,
  onInputChange,
  preserveUnparsedInput = false,
  as: Element = FormInputText,
  onBlur: externalOnBlur,
  ...propsToElement
}: FormInputWithParseOnBlurProps<Value>) => {
  const [input, setInput] = useState<string>($toDisplay(value as Value)); // TODO: Remove cast

  const handleBlur: React.FocusEventHandler<HTMLInputElement> = useCallback((e) => {
    const fromDisplayValue = $fromDisplay(input);
    onChange?.(fromDisplayValue);
    const renderedValue = $toDisplay(fromDisplayValue);
    setInput(preserveUnparsedInput && input && !renderedValue ? input : renderedValue);
    externalOnBlur?.(e);
  }, [input, onChange, $fromDisplay, $toDisplay, externalOnBlur, preserveUnparsedInput]);

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = useCallback((event) => {
    setInput(event.target.value);
    onInputChange?.(event.target.value);
  }, [onInputChange]);

  return (
    <Element
      value={toDisplayPartial(input)}
      onBlur={handleBlur}
      onChange={handleChange}
      {...propsToElement}
    />
  );
};

type FormInputWithParseWithFormikProps<Value extends InputValue> =
  FormInputWithParseProps<Value> & {
    // Formik field name
    name: string;
  };

/**
 * Same as FormInputWithParse, except that the `onChange` hook is wired up to Formik's
 * setValue handler to bypass type issues (since HTML form values can only be strings).
 */
const FormInputWithParseWithFormik = <Value extends InputValue>({
  name,
  onChange,
  $fromDisplay,
  ...propsToFormInput
}: FormInputWithParseWithFormikProps<Value>) => {
  const [field, , helpers] = useField(name);

  const handleChangeWithFormik = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      await Promise.all([
        helpers.setTouched(true),
        ...($fromDisplay
          ? [helpers.setValue($fromDisplay((event.target as HTMLInputElement).value))]
          : []),
      ]);

      onChange?.(event);
    },
    [$fromDisplay, helpers, onChange]
  );

  return (
    <FormInputWithParse
      value={field.value}
      onChange={handleChangeWithFormik}
      $fromDisplay={$fromDisplay}
      {...propsToFormInput}
    />
  );
};

type FormInputWithParseOnBlurWithFormikProps<Value extends InputValue> =
  FormInputWithParseOnBlurProps<Value> & {
    // Formik field name
    name: string;
  };

const FormInputWithParseOnBlurWithFormik = <Value extends InputValue>({
  name,
  ...propsToFormInput
}: FormInputWithParseOnBlurWithFormikProps<Value>) => {
  const [field, , helpers] = useField(name);

  const handleChangeWithFormik = useCallback(
    (parsedValue: unknown) =>
      Promise.all([helpers.setTouched(true), helpers.setValue(parsedValue)]),
    [helpers]
  );

  return (
    <FormInputWithParseOnBlur<Value>
      onChange={handleChangeWithFormik}
      value={field.value}
      {...propsToFormInput}
    />
  );
};

/**
 * Same as FormInputText, except that it hijacks rendering and onChange to render
 * the values as a pretty phone number, prevents non-number inputs, and limits length to 10.
 */
export const FormInputPhoneNumber = (props: FormInputWithParseProps<string | undefined>) => (
  <FormInputWithParse<string | undefined>
    $toDisplay={renderPhoneNumber}
    $fromDisplay={parsePhoneNumber}
    placeholder="(___) ___-____"
    inputMode="numeric"
    autoComplete="tel"
    data-hj-allow={true}
    {...props}
  />
);

/**
 * Same as FormInputText, except that it hijacks rendering and onChange to render
 * the values as a pretty phone number, prevents non-number inputs, and limits length to 10.
 */
export const FormInputDOB = (props: FormInputWithParseProps<string | undefined>) => (
  <FormInputWithParse<string | undefined>
    $toDisplay={toDOBDisplay}
    $fromDisplay={fromDOBDisplay}
    placeholder="MM/DD/YYYY"
    inputMode="numeric"
    autoComplete="bday"
    data-testid="dob"
    data-hj-allow={true}
    {...props}
  />
);

/**
 * A FormInputText, with Formik handling, that parses the dollar amount as a number, in cents.
 *
 * A non-Formik version is not available because the value type is a number. Do not wrap in
 * a ValidatedFormikField. Use a ValidatedFormikComponent instead.
 */
export const FormInputDollarAmountWithFormik = (
  props: FormInputWithParseWithFormikProps<number | undefined>
) => (
  <FormInputWithParseWithFormik<number | undefined>
    $toDisplay={renderDollarAmount}
    $fromDisplay={cleanInteger}
    placeholder="$x.xx"
    inputMode="numeric"
    {...props}
  />
);

/**
 * A FormInputText, with Formik handling, that parses the value as a number.
 *
 * A non-Formik version is not available because the value type is a number. Do not wrap in
 * a ValidatedFormikField. Use a ValidatedFormikComponent instead.
 */
export const FormInputNumberWithFormik = (
  props: FormInputWithParseWithFormikProps<number | undefined>
) => (
  <FormInputWithParseWithFormik<number | undefined>
    $toDisplay={(input) => input?.toString() ?? "0"}
    $fromDisplay={cleanInteger}
    placeholder="0"
    inputMode="numeric"
    {...props}
  />
);

/**
 * A FormInputText, with Formik handling, that parses the value into a "HH:mm" string.
 */
export const FormInputTimeWithFormik = (props: FormInputWithParseOnBlurWithFormikProps<string>) => (
  <FormInputWithParseOnBlurWithFormik<string>
    $toDisplay={(input) => input ?? ""}
    $fromDisplay={(input) => input as string} // TODO: Remove cast
    toDisplayPartial={formatTime}
    type="time"
    {...props}
  />
);

/**
 * A FormInputText, with Formik handling, that parses a typed-in date, as a DateTime.
 *
 * A non-Formik version is not available because the value type is a DateTime. Do not wrap in
 * a ValidatedFormikField. Use a ValidatedFormikComponent instead.
 */
function parseDateForValidation(input?: string): DateTime | undefined {
  const parsed = parseDate(input);
  // An invalid nonempty date must not retain an earlier valid value or pass
  // optional-date validation as an empty answer.
  return parsed ?? (formatDate(input) ? DateTime.invalid("Invalid date") : undefined);
}

export const FormInputDateWithFormik = ({
  parseOnChange = false,
  ...props
}: Omit<
  React.ComponentProps<typeof FormInputWithParseOnBlurWithFormik<DateTime | null | undefined>>,
  "onInputChange" | "preserveUnparsedInput"
> & {
  /** Publish edits for live validation, while keeping formatting and touched state on blur. */
  parseOnChange?: boolean;
}) => {
  const [, , helpers] = useField(props.name);
  const handleInputChange = useCallback((input: string) => {
    void helpers.setValue(parseDateForValidation(input));
  }, [helpers]);

  return (
    <FormInputWithParseOnBlurWithFormik<DateTime | null | undefined>
      $toDisplay={(input) => renderDate(input ?? null)}
      $fromDisplay={parseOnChange ? parseDateForValidation : parseDate}
      toDisplayPartial={formatDate}
      placeholder="MM/DD/YYYY"
      {...props}
      onInputChange={parseOnChange ? handleInputChange : undefined}
      preserveUnparsedInput={parseOnChange}
    />
  );
};

export const FormInputSelectOptions = ({
  options,
  showPlaceholder,
  placeholderText = "Select one...",
  ...formInputSelectProps
}: FormInputSelectProps & {
  options: Record<string, string>;
  showPlaceholder?: boolean;
  placeholderText?: string;
}) => {
  const selectOptions = useMemo(
    () =>
      map(options, (value, key) => (
        <option key={key} value={key} data-testid={`option-${key}`}>
          {value}
        </option>
      )),
    [options]
  );
  return (
    <FormInputSelect {...formInputSelectProps}>
      {showPlaceholder && (
        <option disabled value="">
          {placeholderText}
        </option>
      )}
      {selectOptions}
    </FormInputSelect>
  );
};

type FormInputSelectProps = Omit<React.ComponentProps<typeof FormInputText>, "as">;

const FormInputSelect = (props: FormInputSelectProps) => <FormInputText as="select" {...props} />;

export const FormInputGroup = styled("div")`
  padding: 1rem;
  border: 1px solid #cecbdd;
  border-radius: 8px;

  font-size: 1.1rem;
  background: #fff;
  box-shadow: 0 2px 3px rgba(0, 0, 0, 0.02);
`;
